import { Navigate, createBrowserRouter, useLocation } from "react-router-dom";
import type { ReactNode } from "react";

import { AppShell } from "./components/layout/AppShell";
import { Loading } from "./components/ui";
import { AuthProvider, useAuth } from "./lib/AuthContext";
import {
  OrganizationProvider,
  useCurrentOrganization,
} from "./lib/OrganizationContext";
import { AcceptInvitePage } from "./pages/AcceptInvite";
import { AiSettingsPage } from "./pages/AiSettings";
import { DashboardPage } from "./pages/Dashboard";
import { ContextPreviewPage } from "./pages/ContextPreview";
import { GapsPage } from "./pages/Gaps";
import { IntegrationsPage } from "./pages/Integrations";
import AssistantPage from "./pages/Assistant";
import { InterviewPage } from "./pages/Interview";
import { InterviewsPage } from "./pages/Interviews";
import { KnowledgePage } from "./pages/Knowledge";
import { OnboardingPage } from "./pages/Onboarding";
import { OnboardingReviewPage } from "./pages/OnboardingReview";
import { OnboardingWizardPage } from "./pages/OnboardingWizard";
import { QuestionLibraryPage } from "./pages/QuestionLibrary";
import { SettingsPage } from "./pages/Settings";
import { SignInPage } from "./pages/SignIn";
import { SystemsPage } from "./pages/Systems";
import { TasksPage } from "./pages/Tasks";
import { TeamPage } from "./pages/Team";
import { UploadsPage } from "./pages/Uploads";
import { WorkflowsPage } from "./pages/Workflows";

/**
 * Everything inside the shell needs a signed-in user — unless this deployment has enforcement
 * switched off, in which case a sign-in wall would be theatre in front of an API that ignores it.
 */
function RequireAuth({ children }: { children: ReactNode }) {
  const { loading, signedIn, authRequired } = useAuth();
  const location = useLocation();

  if (loading) return <Loading label="Checking your session…" />;
  if (authRequired && !signedIn) {
    return (
      <Navigate
        to="/signin"
        replace
        state={{ from: location.pathname + location.search }}
      />
    );
  }
  return <>{children}</>;
}

function Shell() {
  return (
    <RequireAuth>
      <OrganizationProvider>
        <AppShell />
      </OrganizationProvider>
    </RequireAuth>
  );
}

/**
 * Where a signed-in person lands depends on what they may do here: somebody who can only answer
 * (a contributor) starts at their tasks, not at an operator's dashboard.
 */
function Landing() {
  const { can } = useAuth();
  const { organizationId } = useCurrentOrganization();
  if (
    organizationId &&
    !can(organizationId, "knowledge:read") &&
    can(organizationId, "tasks:read")
  ) {
    return <Navigate to="/tasks" replace />;
  }
  return <DashboardPage />;
}

function Public({ children }: { children: ReactNode }) {
  return <AuthProvider>{children}</AuthProvider>;
}

export const router = createBrowserRouter([
  // Reachable without an account: an invitee has no login until they accept.
  {
    path: "/signin",
    element: (
      <Public>
        <SignInPage />
      </Public>
    ),
  },
  {
    path: "/invite/:token",
    element: (
      <Public>
        <AcceptInvitePage />
      </Public>
    ),
  },
  {
    path: "/",
    element: (
      <AuthProvider>
        <Shell />
      </AuthProvider>
    ),
    children: [
      { index: true, element: <Landing /> },
      { path: "onboarding", element: <OnboardingPage /> },
      { path: "onboarding/new", element: <OnboardingWizardPage /> },
      { path: "onboarding/review", element: <OnboardingReviewPage /> },
      { path: "tasks", element: <TasksPage /> },
      { path: "interviews", element: <InterviewsPage /> },
      { path: "interviews/:sessionId", element: <InterviewPage /> },
      { path: "question-library", element: <QuestionLibraryPage /> },
      { path: "uploads", element: <UploadsPage /> },
      { path: "assistant", element: <AssistantPage /> },
      { path: "knowledge", element: <KnowledgePage /> },
      { path: "gaps", element: <GapsPage /> },
      { path: "systems", element: <SystemsPage /> },
      { path: "workflows", element: <WorkflowsPage /> },
      { path: "integrations", element: <IntegrationsPage /> },
      { path: "context-preview", element: <ContextPreviewPage /> },
      {
        path: "data-dictionary",
        element: <Navigate to="/context-preview" replace />,
      },
      { path: "team", element: <TeamPage /> },
      { path: "ai", element: <AiSettingsPage /> },
      { path: "settings", element: <SettingsPage /> },
    ],
  },
]);
